import { AgentChat, CadstromObjectType, isCadstromUidOfType } from '@cadstrom/cadstrom-shared';
import Chip from '@mui/material/Chip';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

import { RelativeDate } from '#components/relative-timestamp/relative-date.js';
import { AgentWorkbenchPanel } from '#screens/agent-workbench/agent-workbench-panel.js';
import { AgentWorkbenchRuntimeProvider } from '#screens/agent-workbench/agent-workbench-runtime-provider.js';
import { buildAgentWorkbenchThreadListAdapter } from '#screens/agent-workbench/agent-workbench-thread-list-adapter.js';
import { useTRPC, useTRPCClient } from '#src/trpc.js';
import { isValidAgentConversationRoute } from './agent-conversation-route-utils.js';
import { ConversationFamilyNavigation } from './conversation-family-navigation.js';
import { ConversationTokenUsageSummary } from './conversation-token-usage-summary.js';
import { aggregateConversationTokenUsage } from './conversation-token-usage.js';
import { ConversationUnavailable } from './conversation-unavailable.js';
import { Identifier } from './identifier.js';

import type { AgentWorkbenchAnalyticsContext } from '#screens/agent-workbench/agent-workbench-analytics.js';

/** Workbench events from this surface; the part or step in view rides along from the page context. */
const AGENT_CONVERSATIONS_ANALYTICS: AgentWorkbenchAnalyticsContext = { source: 'agent_conversations' };

export const Route = createFileRoute('/_authorization-gate/user/$ownerId/conversations/$conversationId')({
	beforeLoad: ({ context: { trpc }, params: { ownerId, conversationId } }) => {
		if (!isValidAgentConversationRoute(ownerId, conversationId)) {
			throw notFound();
		}
		return {
			getConversationOptions: trpc.agentChatConversation.get.queryOptions({
				ownerId,
				conversationId,
				includeRelations: true,
			}),
			getMessagesOptions: trpc.agentChatConversation.getMessages.queryOptions({ conversationId }),
		};
	},
	loader: async ({ context: { queryClient, getConversationOptions, getMessagesOptions } }) => {
		const conversation = await queryClient.ensureQueryData(getConversationOptions);
		if (!conversation) {
			throw notFound();
		}
		await queryClient.ensureQueryData(getMessagesOptions);
		return { conversation };
	},
	head: ({ loaderData }) => ({
		meta: [{ title: `${loaderData?.conversation.title ?? 'Agent conversation'} • Cadstrom` }],
	}),
	notFoundComponent: ConversationUnavailable,
	component: AgentConversationPage,
});

function AgentConversationPage() {
	const { ownerId, conversationId } = Route.useParams();
	const { conversation } = Route.useLoaderData();
	const navigate = useNavigate({ from: Route.fullPath });
	const trpc = useTRPC();
	const trpcClient = useTRPCClient();
	const threadList = useMemo(
		() =>
			buildAgentWorkbenchThreadListAdapter({
				// Inert: this page always opens an existing conversation and sets
				// allowThreadCreation={false}, so no thread is ever created here.
				agentId: AgentChat.CHAT_SURFACE_AGENT_IDS.default,
				ownerId,
				trpcClient,
			}),
		[ownerId, trpcClient],
	);
	const { data: currentUser } = useSuspenseQuery(trpc.user.getCurrent.queryOptions());
	const { data: messages } = useSuspenseQuery(trpc.agentChatConversation.getMessages.queryOptions({ conversationId }));
	const modelsUsed = useMemo(
		() => [...new Set(messages.flatMap((message) => (message.metadata?.model ? [message.metadata.model.model] : [])))],
		[messages],
	);
	const tokenUsage = useMemo(() => aggregateConversationTokenUsage(messages), [messages]);
	const isCurrentUserOwner = currentUser.actor.userId === ownerId;
	const isHumanOwner = isCadstromUidOfType(ownerId, CadstromObjectType.User);
	const { data: ownerLookup } = useQuery(
		trpc.user.get.queryOptions(
			{ id: ownerId },
			{
				enabled: !isCurrentUserOwner && isHumanOwner,
				throwOnError: false,
			},
		),
	);
	const ownerDisplayName = currentUser.memberships.find((membership) => membership.groupId === ownerId)?.groupName;
	const userMessageAuthor =
		isCurrentUserOwner ? undefined : (ownerLookup?.user ?? { id: ownerId, displayName: ownerDisplayName ?? ownerId });

	return (
		<div className='flex min-h-0 grow flex-col overflow-hidden bg-gray-2'>
			<header className='flex shrink-0 flex-col gap-3 border-b border-gray-a6 px-4 py-3'>
				<div className='flex flex-wrap items-center gap-2'>
					<h1 className='truncate min-w-0 flex-1 text-lg font-semibold text-gray-12'>
						{conversation.title ?? 'Agent conversation'}
					</h1>
					<div className='flex min-w-0 flex-wrap items-center justify-end gap-3'>
						<ConversationFamilyNavigation
							ownerId={ownerId}
							parentConversation={conversation.parentConversation}
							childConversations={conversation.childConversations}
						/>
						{!conversation.canInteract && <Chip label='Read only' size='small' variant='outlined' />}
					</div>
				</div>

				<div className='flex flex-wrap gap-x-6 gap-y-2 text-sm'>
					<Identifier label='Owner' value={ownerId} />
					<Identifier label='Conversation' value={conversationId} />
					<div className='flex items-center gap-1.5 text-gray-11'>
						<span>Created</span>
						<RelativeDate date={conversation.createdAt} className='text-gray-12' />
					</div>
					<div className='flex items-center gap-1.5 text-gray-11'>
						<span>Last activity</span>
						<RelativeDate date={conversation.lastActivityAt} className='text-gray-12' />
					</div>
					{modelsUsed.length > 0 && (
						<div className='flex min-w-0 items-center gap-1.5 text-gray-11'>
							<span>{modelsUsed.length === 1 ? 'Model' : 'Models'}</span>
							<span className='font-mono text-xs text-gray-12'>{modelsUsed.join(', ')}</span>
						</div>
					)}
				</div>
				{tokenUsage && <ConversationTokenUsageSummary usage={tokenUsage} />}
			</header>

			<div className='flex min-h-0 grow flex-col'>
				<AgentWorkbenchRuntimeProvider
					analytics={AGENT_CONVERSATIONS_ANALYTICS}
					key={conversationId}
					adapter={threadList.adapter}
					listReady={threadList.listReady}
					initialConversationId={conversationId}
				>
					<AgentWorkbenchPanel
						placeholder='Continue this conversation…'
						userMessageAuthor={userMessageAuthor}
						interactionMode={conversation.canInteract ? 'interactive' : 'read-only-show-chrome'}
						allowThreadCreation={false}
						allowThreadDeletion={false}
						emptyStateMessage={
							conversation.canInteract ?
								'This conversation is empty. Send a message to begin.'
							:	'This conversation has no messages.'
						}
						onSelectThread={(selectedConversationId) => {
							if (selectedConversationId !== conversationId) {
								void navigate({ params: { ownerId, conversationId: selectedConversationId } });
							}
						}}
					/>
				</AgentWorkbenchRuntimeProvider>
			</div>
		</div>
	);
}
